import {
  alpha,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as clientSlice from '../../../data/client.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { PassengerIngestionExecutionStatus } from '../../../data/models/passenger-ingestion.model';
import { DataGridPro } from '../../tables/DataGridPro';
import {
  GridCellParams,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import dateUtils from '../../../services/DateUtils';
import { DateTime } from 'luxon';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'PassengerIngestion';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  subHeader: `${PREFIX}-subHeader`,
  clientSelect: `${PREFIX}-clientSelect`,
  statusSelect: `${PREFIX}-statusSelect`,
  restartButton: `${PREFIX}-restartButton`,
  chip: `${PREFIX}-chip`,
  runningChip: `${PREFIX}-runningChip`,
  succeededChip: `${PREFIX}-succeededChip`,
  failedChip: `${PREFIX}-failedChip`,
  grid: `${PREFIX}-grid`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(3),
  },
  [`& .${classes.header}`]: {
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.clientSelect}`]: {
    width: 260,
    marginRight: theme.spacing(2),
  },
  [`& .${classes.statusSelect}`]: {
    width: 200,
  },
  [`& .${classes.subHeader}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.restartButton}`]: {
    height: 32,
  },
  [`& .${classes.chip}`]: {
    width: 90,
  },
  [`& .${classes.runningChip}`]: {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    '& .MuiChip-label': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  [`& .${classes.succeededChip}`]: {
    backgroundColor: alpha(theme.palette.success.main, 0.2),
    '& .MuiChip-label': {
      color: `${theme.palette.success.main} !important`,
    },
  },
  [`& .${classes.failedChip}`]: {
    backgroundColor: alpha(theme.palette.error.main, 0.2),
    '& .MuiChip-label': {
      color: `${theme.palette.error.main} !important`,
    },
  },
  [`& .${classes.grid}`]: {
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: '10px',
    },
  },
}));

export default function PassengerIngestion() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isSuperReaderUser = useIsSuperReaderUser();
  const allStatuses = 'allStatuses';
  const clients = useAppSelector(clientSlice.selectClients);
  const executions = useAppSelector(
    clientSlice.selectPassengerIngestionExecutions,
  );
  const [clientBC, setClientBC] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(allStatuses);
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);
  const [showStartIngestionConfirm, setShowStartIngestionConfirm] =
    useState(false);
  const [showRestartExecutionConfirm, setShowRestartExecutionConfirm] =
    useState(false);

  useEffect(() => {
    dispatch(clientSlice.fetchClientsAsync());
  }, []);

  useEffect(() => {
    if (clientBC) {
      const status =
        selectedStatus === allStatuses
          ? undefined
          : (selectedStatus as PassengerIngestionExecutionStatus);
      dispatch(
        clientSlice.fetchPassengerIngestionExecutionsAsync(clientBC, status),
      );
    }
  }, [clientBC, selectedStatus]);

  const statuses = Object.entries(PassengerIngestionExecutionStatus).map(
    ([key, value]) => ({
      key,
      value: value as string,
    }),
  );
  statuses.unshift({
    key: allStatuses,
    value: allStatuses,
  });

  const handleClientChange = (clientBC: string) => {
    setClientBC(clientBC);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleStartIngestionClick = () => {
    setShowStartIngestionConfirm(true);
  };

  const handleRestartExecutionClick = (executionId: string) => {
    setShowRestartExecutionConfirm(true);
  };

  const handleStartIngestionConfirm = () => {
    // TODO: Call API to start ingestion
    setShowStartIngestionConfirm(false);
  };

  const handleRestartExecutionConfirm = () => {
    // TODO: Call API to restart execution
    setShowRestartExecutionConfirm(false);
  };

  const handleExecutionClick = (params: GridRowParams) => {
    navigate(`/passenger-ingestion/${params.row.id}?clientBC=${clientBC}`);
  };

  const renderStatus = (params: GridCellParams): React.ReactElement => {
    const className = `${params.row.status}Chip` as
      | 'runningChip'
      | 'succeededChip'
      | 'failedChip';
    return (
      <Chip
        className={`${classes[className]} ${classes.chip}`}
        label={t(`passengerIngestion.${params.row.status}`)}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'time',
      headerName: t('passengerIngestion.time'),
      sortable: true,
      minWidth: 180,
      valueFormatter: (params) =>
        dateUtils.formatDateTime(DateTime.fromISO(params.value)),
    },
    {
      field: 'id',
      headerName: t('passengerIngestion.id'),
      sortable: false,
    },
    {
      field: 'step',
      headerName: t('passengerIngestion.step'),
      minWidth: 250,
      sortable: false,
    },
    {
      field: 'status',
      headerName: t('passengerIngestion.status'),
      sortable: false,
      minWidth: 150,
      renderCell: renderStatus,
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      renderCell: (params) => {
        // Show restart button on hover if execution failed
        if (
          params.row.id === hoveredRow &&
          params.row.status === PassengerIngestionExecutionStatus.Failed
        ) {
          return (
            <Button
              className={classes.restartButton}
              color="primary"
              variant="contained"
              onClick={() => handleRestartExecutionClick(params.row.id)}>
              {t('passengerIngestion.restart')}
            </Button>
          );
        }
      },
    },
  ];

  return (
    <StyledPaper elevation={2}>
      <Stack direction="column" className={classes.container}>
        <Stack direction="row" className={classes.header}>
          <Typography variant="h3" className={classes.title}>
            {t('passengerIngestion.title')}
          </Typography>
        </Stack>
        <Stack direction="row" className={classes.subHeader}>
          <Stack direction="row">
            <TextField
              select
              className={classes.clientSelect}
              label={t('passengerIngestion.selectClient')}
              variant="outlined"
              required={true}
              value={clientBC}
              onChange={(evt) => handleClientChange(evt.target.value)}>
              {clients?.map((client) => {
                return (
                  <MenuItem value={client.clientBC} key={client.clientBC}>
                    {client.legalName}
                  </MenuItem>
                );
              })}
            </TextField>
            {clientBC && (
              <TextField
                select
                className={classes.statusSelect}
                label={t('passengerIngestion.status')}
                variant="outlined"
                required={true}
                value={selectedStatus}
                onChange={(evt) => handleStatusChange(evt.target.value)}>
                {statuses?.map((status) => {
                  return (
                    <MenuItem value={status.key} key={status.key}>
                      {t(`passengerIngestion.${status.value}`)}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
          </Stack>
          {!isSuperReaderUser && clientBC && (
            <Button
              color="primary"
              variant="contained"
              onClick={handleStartIngestionClick}>
              {t('passengerIngestion.startIngestion')}
            </Button>
          )}
        </Stack>
        {clientBC && (
          <DataGridPro
            className={classes.grid}
            disableColumnMenu={true}
            getRowId={(row) => row.id}
            rows={executions}
            columns={columns}
            rowCount={executions.length}
            pagination={true}
            autoHeight
            onRowClick={handleExecutionClick}
            slotProps={{
              row: {
                onMouseEnter: (event) => {
                  const id = event.currentTarget.dataset.id;
                  setHoveredRow(id);
                },
                onMouseLeave: () => {
                  setHoveredRow(undefined);
                },
              },
            }}
          />
        )}
      </Stack>
      <ConfirmationDialog
        title={t('passengerIngestion.confirmStartIngestionTitle')}
        message={t('passengerIngestion.confirmStartIngestionMessage')}
        open={showStartIngestionConfirm}
        positiveAction={handleStartIngestionConfirm}
        negativeAction={() => setShowStartIngestionConfirm(false)}
      />
      <ConfirmationDialog
        title={t('passengerIngestion.confirmRestartExecutionTitle')}
        message={t('passengerIngestion.confirmRestartExecutionMessage')}
        open={showRestartExecutionConfirm}
        positiveAction={handleRestartExecutionConfirm}
        negativeAction={() => setShowRestartExecutionConfirm(false)}
      />
    </StyledPaper>
  );
}
